import type React from "react";
import type { AssistantSessionSummary } from "./types";

// Lowercase: browsers lowercase DataTransfer types, so `types.includes` must match.
const SESSION_DRAG_TYPE = "application/x-codesmith-session";

export function startSessionDrag(e: React.DragEvent, sessionId: string) {
  e.dataTransfer.setData(SESSION_DRAG_TYPE, sessionId);
  e.dataTransfer.effectAllowed = "move";
}

/** Readable during dragover, when the payload itself is still hidden. */
export function isSessionDrag(e: React.DragEvent) {
  return Array.from(e.dataTransfer.types).includes(SESSION_DRAG_TYPE);
}

export function draggedSessionId(e: React.DragEvent) {
  return e.dataTransfer.getData(SESSION_DRAG_TYPE) || null;
}

/**
 * Ids an optimistic drop moves into a project, mirroring the backend's
 * adoption: the session and the workers under it that are in no project.
 */
export function sessionsAdoptedBy(
  sessions: AssistantSessionSummary[],
  sessionId: string,
): Set<string> {
  const adopted = new Set([sessionId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const s of sessions) {
      if (
        !adopted.has(s.id) &&
        !s.project_id &&
        s.parent_session_id &&
        adopted.has(s.parent_session_id)
      ) {
        adopted.add(s.id);
        grew = true;
      }
    }
  }
  return adopted;
}

export function adoptSessionIntoProject(
  sessions: AssistantSessionSummary[],
  adoptedIds: Set<string>,
  projectId: string,
): AssistantSessionSummary[] {
  return sessions.map((s) =>
    adoptedIds.has(s.id) ? { ...s, project_id: projectId } : s,
  );
}

/** Undoes one drop: its adopted rows still in `projectId` leave it. */
export function revertProjectAdoption(
  sessions: AssistantSessionSummary[],
  adoptedIds: Set<string>,
  projectId: string,
): AssistantSessionSummary[] {
  return sessions.map((s) =>
    adoptedIds.has(s.id) && s.project_id === projectId
      ? { ...s, project_id: null }
      : s,
  );
}
